/**
 * NC-1294 Slice 2 (FE) — Ist-Kosten und Abweichung zur Schätzung.
 *
 * Three states are shown as three different statements, never collapsed:
 *
 *   available=false                  → "keine Daten" — the source could not be read
 *   available, hasActuals=false      → "noch nicht gebucht" — cost-daily is empty
 *                                      until the #1290 export runs, and Cost
 *                                      Management lags 8–24 h
 *   hasActuals                       → the figure, plus the deviation
 *
 * An Ist of 0 must never read as "100 % under budget", which is why the API sends
 * `comparison: null` in the first two cases and this component honours that instead
 * of computing its own.
 *
 * The provenance label is not decoration: with SubscriptionCostDisabled on all three
 * subscriptions the export derives cost as quantity × PAYG list price. Presenting
 * that as an invoiced amount would give false confidence about real money.
 */

import React from 'react';
import { Box, Chip, Divider, Tooltip, Typography } from '@mui/material';
import { RunActualCost, RunCostComparison, CostSource } from '../services/types';

interface ActualCostSectionProps {
  actualCost?: RunActualCost;
  comparison?: RunCostComparison | null;
}

const SOURCE_LABEL: Record<CostSource, string> = {
  billed: 'abgerechnet',
  'derived-payg': 'abgeleitet aus Listenpreisen',
  mixed: 'gemischte Quellen',
};

const SOURCE_HINT: Record<CostSource, string> = {
  billed: 'Wert stammt aus der abgerechneten Kostenspalte.',
  'derived-payg':
    'Kein abgerechneter Wert verfügbar (Kostensichtbarkeit der Subscription gesperrt) — ' +
    'berechnet als Menge x Listenpreis. Gute Schätzung des Listenpreises, keine Rechnung.',
  mixed: 'Die Tage dieses Laufs stammen teils aus abgerechneten, teils aus abgeleiteten Werten.',
};

const formatEur = (value: number, currency: string): string => `${value.toFixed(2)} ${currency}`;

export const ActualCostSection: React.FC<ActualCostSectionProps> = ({ actualCost, comparison }) => {
  // Pre-NC-1294 API responses carry no actualCost at all — render nothing rather
  // than an empty section that looks like a load failure.
  if (!actualCost) return null;

  return (
    <Box sx={{ mt: 2 }} data-testid="actual-cost-section">
      <Divider sx={{ my: 2 }} />
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Ist-Kosten
      </Typography>

      {!actualCost.available ? (
        <Typography variant="body2" data-testid="actual-cost-unavailable">
          Keine Daten — die Kostenquelle ist derzeit nicht abrufbar.
        </Typography>
      ) : !actualCost.hasActuals ? (
        // Deliberately NOT "0 EUR": nothing is booked yet, which is a different
        // statement from "this run cost nothing".
        <Typography variant="body2" data-testid="actual-cost-pending">
          Noch nicht gebucht — Kostendaten erscheinen mit ein bis zwei Tagen Verzug.
        </Typography>
      ) : (
        <Box data-testid="actual-cost-value">
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h6">{formatEur(actualCost.istEur, actualCost.currency)}</Typography>
            {actualCost.costSource && (
              <Tooltip title={SOURCE_HINT[actualCost.costSource]}>
                <Chip
                  label={SOURCE_LABEL[actualCost.costSource]}
                  size="small"
                  variant="outlined"
                  color={actualCost.costSource === 'billed' ? 'success' : 'warning'}
                  data-testid="actual-cost-source"
                />
              </Tooltip>
            )}
          </Box>

          <Typography variant="caption" color="text.secondary" component="div">
            {actualCost.days} Tag{actualCost.days === 1 ? '' : 'e'} gebucht
            {actualCost.firstDate && actualCost.lastDate
              ? ` (${actualCost.firstDate} bis ${actualCost.lastDate})`
              : ''}
          </Typography>

          {actualCost.mixedCurrency ? (
            // Summing across currencies is not meaningful, so no comparison is drawn.
            <Typography variant="caption" color="warning.main" component="div" data-testid="actual-cost-mixed-currency">
              Mehrere Währungen enthalten — kein Vergleich zur Schätzung möglich.
            </Typography>
          ) : (
            comparison && (
              <Typography variant="body2" sx={{ mt: 0.5 }} data-testid="actual-cost-deviation">
                {comparison.abweichungEur >= 0 ? 'Über' : 'Unter'} Schätzung:{' '}
                <strong>
                  {formatEur(Math.abs(comparison.abweichungEur), actualCost.currency)}
                </strong>
                {comparison.abweichungPct !== null
                  ? ` (${comparison.abweichungPct >= 0 ? '+' : ''}${comparison.abweichungPct} %)`
                  : ' — keine Schätzung zum Vergleich'}
              </Typography>
            )
          )}
        </Box>
      )}
    </Box>
  );
};

export default ActualCostSection;
